import { defineStore } from 'pinia';
import { ref } from 'vue';

const WELCOME_SEEN_KEY = 'avp.welcomeGuide.seen.v1';

function readSeenSet(): Set<string> {
  try {
    const raw = window.localStorage.getItem(WELCOME_SEEN_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return new Set(parsed.filter((v) => typeof v === 'string'));
  } catch {
    // ignore malformed payload
  }
  return new Set();
}

function writeSeenSet(set: Set<string>): void {
  try {
    window.localStorage.setItem(WELCOME_SEEN_KEY, JSON.stringify(Array.from(set)));
  } catch {
    // ignore storage errors (private mode, quota)
  }
}

export const useUiStore = defineStore('ui', () => {
  const copilotOpen = ref(false);
  const sidebarCollapsed = ref(false);
  const mobileNavOpen = ref(false);
  const welcomeGuideOpen = ref(false);

  function openCopilot(): void {
    copilotOpen.value = true;
  }

  function closeCopilot(): void {
    copilotOpen.value = false;
  }

  function toggleCopilot(): void {
    copilotOpen.value = !copilotOpen.value;
  }

  function toggleSidebar(): void {
    sidebarCollapsed.value = !sidebarCollapsed.value;
  }

  function openMobileNav(): void {
    mobileNavOpen.value = true;
  }

  function closeMobileNav(): void {
    mobileNavOpen.value = false;
  }

  function toggleMobileNav(): void {
    mobileNavOpen.value = !mobileNavOpen.value;
  }

  function openWelcomeGuide(): void {
    welcomeGuideOpen.value = true;
  }

  function closeWelcomeGuide(): void {
    welcomeGuideOpen.value = false;
  }

  function hasSeenWelcomeGuide(userKey: string | undefined): boolean {
    if (!userKey) return true; // anonymous / pre-auth: don't auto-open
    return readSeenSet().has(userKey);
  }

  function markWelcomeGuideSeen(userKey: string | undefined): void {
    if (!userKey) return;
    const set = readSeenSet();
    if (set.has(userKey)) return;
    set.add(userKey);
    writeSeenSet(set);
  }

  return {
    copilotOpen,
    sidebarCollapsed,
    mobileNavOpen,
    welcomeGuideOpen,
    openCopilot,
    closeCopilot,
    toggleCopilot,
    toggleSidebar,
    openMobileNav,
    closeMobileNav,
    toggleMobileNav,
    openWelcomeGuide,
    closeWelcomeGuide,
    hasSeenWelcomeGuide,
    markWelcomeGuideSeen,
  };
});
